import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import usePipelineEvents from '../hooks/usePipelineEvents';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { getApiUrl } from '../utils/api';
// import ReservedApps from './ReservedApps'; // Moved to header as favorites

ChartJS.register(ArcElement, Tooltip, Legend);

const Dashboard = ({ onNavigateToApp, onNavigateToSettings, onNavigateToAppVersions }) => {
    const [recentDeployments, setRecentDeployments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [autoRefreshing, setAutoRefreshing] = useState(false);
    const [sseConnected, setSseConnected] = useState(false);
    const [selectedTypologie, setSelectedTypologie] = useState(() => {
        // Try to get the saved filter from localStorage
        const saved = localStorage.getItem('dashboard-typologie-filter');
        return saved || 'ALL';
    });
    const [dashboardStats, setDashboardStats] = useState({
        octoAppsByProvider: {},
        octoAppsByTypology: {},
        octoAppsByVersion: {},
        productionOctoApps: []
    });

    // Typologie options based on backend allowedRoots
    const typologieOptions = ['ALL', 'demo', 'internal', 'perf', 'production', 'recette', 'pov'];

    // Handle typologie filter change
    const handleTypologieChange = (newTypologie) => {
        setSelectedTypologie(newTypologie);
        localStorage.setItem('dashboard-typologie-filter', newTypologie);
    };

    // Real-time pipeline updates
    const handlePipelineUpdate = useCallback((pipelineId, updatedPipeline) => {
        
        
        // Show a toast notification for status changes
        if (updatedPipeline.status && updatedPipeline.status !== 'running') {
            const statusEmoji = {
                'success': '✅',
                'failed': '❌',
                'stopped': '⏹️'
            };
            toast.success(`${statusEmoji[updatedPipeline.status] || '📊'} Pipeline ${pipelineId} ${updatedPipeline.status}`);
        }
        
        setRecentDeployments(prevDeployments => {
            // Find the deployment to update
            const deploymentIndex = prevDeployments.findIndex(deployment => 
                deployment.id === pipelineId
            );
            
            if (deploymentIndex !== -1) {
                // Create a new array with the updated deployment
                const newDeployments = [...prevDeployments];
                // Map backend status to frontend state field
                const mappedUpdate = {
                    ...updatedPipeline,
                    state: updatedPipeline.status // Map status to state for frontend
                };
                newDeployments[deploymentIndex] = { 
                    ...newDeployments[deploymentIndex], 
                    ...mappedUpdate
                };
                return newDeployments;
            }
            
            // If not found in recent deployments, refresh the data
            fetchDashboardData();
            return prevDeployments;
        });
    }, []);

    // Connection status handler
    const handleConnectionChange = useCallback((connected) => {
        setSseConnected(connected);
        
    }, []);

    usePipelineEvents(handlePipelineUpdate, handleConnectionChange);

    useEffect(() => {
        fetchDashboardData();
        
        // Set up auto-refresh for recent deployments every 30 seconds
        const autoRefreshInterval = setInterval(() => {
            setAutoRefreshing(true);
            fetchDashboardData().finally(() => {
                setAutoRefreshing(false);
            });
        }, 30000);
        
        return () => {
            clearInterval(autoRefreshInterval);
        };
    }, []); // Only run on mount

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            
            // Fetch recent deployments from our database
            const deploymentsResponse = await axios.get(getApiUrl('/api/pipeline/status/recent'));
            setRecentDeployments(deploymentsResponse.data || []);

            // Fetch dashboard statistics
            const statsResponse = await axios.get(getApiUrl('/api/dashboard/stats'));
            
            setDashboardStats(statsResponse.data);

        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'SUCCESS':
            case 'success': return 'success';
            case 'RUNNING':
            case 'running': return 'primary';
            case 'FAILED':
            case 'failed': return 'danger';
            case 'STOPPED':
            case 'stopped': return 'warning';
            default: return 'secondary';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'SUCCESS':
            case 'success': return 'bi-check-circle-fill';
            case 'RUNNING':
            case 'running': return 'bi-arrow-clockwise';
            case 'FAILED':
            case 'failed': return 'bi-x-circle-fill';
            case 'STOPPED':
            case 'stopped': return 'bi-pause-circle-fill';
            default: return 'bi-question-circle-fill';
        }
    };

    const formatDuration = (startedAt, completedAt, durationSeconds) => {
        // If we have the actual duration from backend, use that
        if (durationSeconds !== null && durationSeconds !== undefined) {
            return `${Math.round(durationSeconds / 60)} min`;
        }
        
        // Otherwise calculate from start/end times
        if (!startedAt) return 'N/A';
        const start = new Date(startedAt);
        const end = completedAt ? new Date(completedAt) : new Date();
        const duration = Math.round((end - start) / 1000 / 60); // minutes
        return `${duration} min`;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAppInfo = (deployment) => {
        // Handle different possible structures for deployment data
        if (deployment.source && typeof deployment.source === 'object') {
            // Backend provides source as an object with typology, cluster, app properties
            return {
                typology: deployment.source.typology || 'N/A',
                cluster: deployment.source.cluster || 'N/A',
                app: deployment.source.app || 'N/A'
            };
        } else if (typeof deployment.source === 'string') {
            // Fallback for string source
            const pathParts = deployment.source.split('/');
            return {
                typology: pathParts[0] || 'N/A',
                cluster: pathParts[1] || 'N/A',
                app: pathParts[2] || 'N/A'
            };
        } else if (deployment.appPath) {
            // Use appPath if available
            const pathParts = deployment.appPath.split('/');
            return {
                typology: pathParts[0] || 'N/A',
                cluster: pathParts[1] || 'N/A',
                app: pathParts[2] || 'N/A'
            };
        } else if (deployment.path) {
            // Use path if available
            const pathParts = deployment.path.split('/');
            return {
                typology: pathParts[0] || 'N/A',
                cluster: pathParts[1] || 'N/A',
                app: pathParts[2] || 'N/A'
            };
        } else {
            return { typology: 'N/A', cluster: 'N/A', app: 'N/A' };
        }
    };

    // Filter deployments based on selected typologie
    const filteredDeployments = recentDeployments.filter(deployment => {
        if (selectedTypologie === 'ALL') return true;
        const appInfo = getAppInfo(deployment);
        return appInfo.typology.toLowerCase() === selectedTypologie.toLowerCase();
    });

    const handleSettings = () => {
        onNavigateToSettings();
    };

    const handleAppVersions = () => {
        onNavigateToAppVersions();
    };

    const handleDeployApp = () => {
        onNavigateToApp();
    };

    const handleStopPipeline = async (pipelineId) => {
        try {
            await axios.post(getApiUrl(`/api/pipeline/stop/${pipelineId}`));
            toast.success('Pipeline stopped successfully');
            fetchDashboardData();
        } catch (error) {
            console.error('Error stopping pipeline:', error);
            toast.error('Failed to stop pipeline');
        }
    };

    const handleUpdatePipelineStatus = async (pipelineId) => {
        try {
            await axios.post(getApiUrl(`/api/pipeline/update-status/${pipelineId}`));
            toast.success('Pipeline status updated');
            fetchDashboardData();
        } catch (error) {
            console.error('Error updating pipeline status:', error);
            toast.error('Failed to update pipeline status');
        }
    };

    const handleExternalLink = (url, name) => {
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success(`Opening ${name}`);
    };

    // Calculate total Octo apps
    const totalOctoApps = Object.values(dashboardStats.octoAppsByProvider).reduce((sum, providerData) => sum + providerData.total, 0);

    // Calculate total apps by version (excluding internal apps)
    const totalAppsByVersion = Object.values(dashboardStats.octoAppsByVersion || {}).reduce((sum, versionData) => {
        if (typeof versionData === 'object' && versionData.byTypology) {
            // Sum up all non-internal apps from byTypology
            return sum + Object.entries(versionData.byTypology)
                .filter(([typology]) => !['internal'].includes(typology.toLowerCase()))
                .reduce((versionSum, [, count]) => versionSum + count, 0);
        } else if (typeof versionData === 'number') {
            return sum + versionData;
        }
        return sum;
    }, 0);

    // Prepare chart data for customer apps by version
    const chartData = {
        labels: Object.keys(dashboardStats.octoAppsByVersion || {}).sort((a, b) => b.localeCompare(a)),
        datasets: [
            {
                data: Object.keys(dashboardStats.octoAppsByVersion || {})
                    .sort((a, b) => b.localeCompare(a))
                    .map(version => {
                        const versionData = dashboardStats.octoAppsByVersion[version];
                        if (typeof versionData === 'object' && versionData.total) {
                            return versionData.total;
                        } else if (typeof versionData === 'number') {
                            return versionData;
                        }
                        return 0;
                    }),
                backgroundColor: [
                    '#FF6384',
                    '#36A2EB',
                    '#FFCE56',
                    '#4BC0C0',
                    '#9966FF',
                    '#FF9F40',
                    '#FF6384',
                    '#C9CBCF'
                ],
                borderColor: 'rgba(255, 255, 255, 0.8)',
                borderWidth: 2,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: 'white',
                    font: {
                        size: 10
                    },
                    usePointStyle: true,
                    padding: 10,
                    generateLabels: (chart) => {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
                            return data.labels.map((label, i) => {
                                const value = data.datasets[0].data[i];
                                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                return {
                                    text: `${label} (${percentage}%)`,
                                    fillStyle: data.datasets[0].backgroundColor[i],
                                    strokeStyle: data.datasets[0].backgroundColor[i],
                                    lineWidth: 0,
                                    pointStyle: 'circle',
                                    hidden: false,
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: 'white',
                bodyColor: 'white',
                borderColor: 'rgba(255, 255, 255, 0.2)',
                borderWidth: 1,
                callbacks: {
                    label: (context) => {
                        const label = context.label || '';
                        const value = context.parsed;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        }
    };

    return (
        <div className="dashboard">
            {/* Hero Section with Actions */}
            <motion.div 
                className="hero-section mb-5"
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
            >
                <div className="row align-items-center">
                    <div className="col-lg-8">
                        <h1 className="display-4 fw-bold text-primary mb-3">
                            <i className="bi bi-rocket me-3"></i>
                            Infrastructure Management
                        </h1>
                        <p className="lead text-muted mb-4">
                            Deploy your applications with confidence and track your infrastructure at a glance
                        </p>
                    </div>
                    <div className="col-lg-4">
                        <div className="d-grid gap-3">
                            <button 
                                className="btn btn-primary btn-lg shadow-sm"
                                onClick={handleDeployApp}
                            >
                                <i className="bi bi-rocket me-2"></i>
                                Deploy App
                            </button>
                            <div className="btn-group w-100">
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={handleAppVersions}
                                >
                                    <i className="bi bi-list-check me-1"></i>
                                    Search Apps
                                </button>
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={handleSettings}
                                >
                                    <i className="bi bi-gear me-1"></i>
                                    Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* External Links Section */}
            <motion.div 
                className="row mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
            >
                <div className="col-12">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body p-4">
                            <h5 className="mb-3 fw-bold text-primary">
                                <i className="bi bi-link-45deg me-2"></i>
                                Quick Links
                            </h5>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <button 
                                        className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => handleExternalLink('https://grafana.observability.frequentiel.com/d/be63d58ssda0wd/versions?orgId=1', 'Azure Monitoring')}
                                    >
                                        <i className="bi bi-cloud me-2"></i>
                                        Azure Monitoring
                                    </button>
                                </div>
                                <div className="col-md-4">
                                    <button 
                                        className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => handleExternalLink('https://grafana.hz.observability.frequentiel.com/d/be63d58ssda0wd/versions?orgId=1', 'Hetzner Monitoring')}
                                    >
                                        <i className="bi bi-server me-2"></i>
                                        Hetzner Monitoring
                                    </button>
                                </div>
                                <div className="col-md-4">
                                    <button 
                                        className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                                        onClick={() => handleExternalLink('https://frequentiel.atlassian.net/jira/dashboards/11081', 'Release Calendar')}
                                    >
                                        <i className="bi bi-calendar-event me-2"></i>
                                        Release Calendar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Metrics Grid */}
            <div className="row g-4 mb-5">


            <motion.div 
                    className="col-lg-4"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                >
                                                                                                                                                                       <div className="metric-card metric-card-deployments text-dark p-4 rounded-4 shadow-lg h-100 position-relative overflow-hidden">
                           <div className="metric-card-bg"></div>
                           <div className="metric-card-content position-relative z-2">
                                                                                               <div className="metric-header mb-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="metric-icon-wrapper">
                                            <i className="bi bi-clock-history display-6"></i>
                                        </div>
                                        <div className="metric-number text-end">
                                            <div className="display-3 fw-bold text-white">{(() => {
                                                // Count only non-internal deployments
                                                return (filteredDeployments || []).filter(deployment => {
                                                    const appInfo = getAppInfo(deployment);
                                                    const typology = appInfo.typology || 'unknown';
                                                    return typology.toLowerCase() !== 'internal';
                                                }).length;
                                            })()}</div>
                                            <div className="h6 text-white text-opacity-90">Recent Deployments</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                          
                                                    <div className="metric-breakdown">
                               {(() => {
                                                                       // Group deployments by month and typology (max 2 months)
                                    const monthlyBreakdown = {};
                                    const now = new Date();
                                    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                                    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                                    
                                    (filteredDeployments || []).forEach(deployment => {
                                        const deploymentDate = new Date(deployment.created_on || deployment.createdAt);
                                        const appInfo = getAppInfo(deployment);
                                        const typology = appInfo.typology || 'unknown';
                                        
                                        // Skip internal deployments
                                        if (typology.toLowerCase() === 'internal') {
                                            return;
                                        }
                                        
                                        // Check if deployment is in this month or last month
                                        if (deploymentDate >= lastMonth) {
                                            let monthKey;
                                            if (deploymentDate >= thisMonth) {
                                                monthKey = 'This Month';
                                            } else {
                                                monthKey = 'Last Month';
                                            }
                                            
                                            if (!monthlyBreakdown[monthKey]) {
                                                monthlyBreakdown[monthKey] = { total: 0, byTypology: {} };
                                            }
                                            
                                            monthlyBreakdown[monthKey].total++;
                                            monthlyBreakdown[monthKey].byTypology[typology] = 
                                                (monthlyBreakdown[monthKey].byTypology[typology] || 0) + 1;
                                        }
                                    });
                                    
                                                                         // Always show both months, even if empty
                                      const sortedMonths = [
                                          ['This Month', monthlyBreakdown['This Month'] || { total: 0, byTypology: {} }],
                                          ['Last Month', monthlyBreakdown['Last Month'] || { total: 0, byTypology: {} }]
                                      ];
                                   
                                   return sortedMonths.map(([month, data]) => (
                                      <div key={month} className="breakdown-section mb-2 p-2 bg-light bg-opacity-90 rounded-3">
                                          <div className="d-flex align-items-center justify-content-between mb-2">
                                              <span className="fw-bold text-dark small text-capitalize">
                                                  {month}
                                              </span>
                                              <span className="badge bg-info bg-opacity-75 fs-6 px-2 py-1">
                                                  {data.total}
                                              </span>
                                          </div>
                                          
                                          <div className="row g-1">
                                              {/* POV Deployments for this month */}
                                              <div className="col-4">
                                                  <div className="metric-counter bg-warning bg-opacity-75 rounded-2 p-1 text-center">
                                                      <div className="h6 fw-bold text-dark mb-0">
                                                          {Object.entries(data.byTypology)
                                                              .filter(([typology]) => ['pov', 'proof-of-concept', 'proofofconcept', 'poc'].includes(typology.toLowerCase()))
                                                              .reduce((sum, [, count]) => sum + count, 0)}
                                                      </div>
                                                      <div className="text-dark fw-semibold small">POV</div>
                                                  </div>
                                              </div>
                                              
                                              {/* Production Deployments for this month */}
                                              <div className="col-4">
                                                  <div className="metric-counter bg-success bg-opacity-75 rounded-2 p-1 text-center">
                                                      <div className="h6 fw-bold text-white mb-0">
                                                          {Object.entries(data.byTypology)
                                                              .filter(([typology]) => ['production', 'prod', 'live'].includes(typology.toLowerCase()))
                                                              .reduce((sum, [, count]) => sum + count, 0)}
                                                      </div>
                                                      <div className="text-white fw-semibold small">Production</div>
                                                  </div>
                                              </div>
                                              
                                              {/* Other Deployments for this month */}
                                              <div className="col-4">
                                                  <div className="metric-counter bg-info bg-opacity-75 rounded-2 p-1 text-center">
                                                      <div className="h6 fw-bold text-white mb-0">
                                                          {Object.entries(data.byTypology)
                                                              .filter(([typology]) => !['pov', 'proof-of-concept', 'proofofconcept', 'poc', 'production', 'prod', 'live', 'internal'].includes(typology.toLowerCase()))
                                                              .reduce((sum, [, count]) => sum + count, 0)}
                                                      </div>
                                                      <div className="text-white fw-semibold small">Other</div>
                                                  </div>
                                              </div>
                                          </div>
                                      </div>
                                  ));
                              })()}
                          </div>
                     </div>
                </motion.div>

                <motion.div 
                    className="col-lg-4"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                >
                                                                                                                                                                       <div className="metric-card metric-card-providers text-dark p-4 rounded-4 shadow-lg h-100 position-relative overflow-hidden">
                           <div className="metric-card-bg"></div>
                                                                                                                                                                           <div
                                                                                                                                                                               className="metric-card-content position-relative z-2">
                                                                                                                                                                               <div
                                                                                                                                                                                   className="metric-header mb-3">
                                                                                                                                                                                   <div
                                                                                                                                                                                       className="d-flex align-items-center justify-content-between">
                                                                                                                                                                                       <div
                                                                                                                                                                                           className="metric-icon-wrapper">
                                                                                                                                                                                           <i className="bi bi-cloud display-6"></i>
                                                                                                                                                                                       </div>
                                                                                                                                                                                       <div
                                                                                                                                                                                           className="metric-number text-end">
                                                                                                                                                                                           <div
                                                                                                                                                                                               className="display-3 fw-bold text-white">{(() => {
                                                                                                                                                                                               // Count providers that have non-internal apps
                                                                                                                                                                                               return Object.entries(dashboardStats.octoAppsByProvider).filter(([provider, providerData]) => {
                                                                                                                                                                                                   const nonInternalApps = Object.entries(providerData.byTypology)
                                                                                                                                                                                                       .filter(([typology]) => !['internal'].includes(typology.toLowerCase()))
                                                                                                                                                                                                       .reduce((sum, [, count]) => sum + count, 0);
                                                                                                                                                                                                   return nonInternalApps > 0;
                                                                                                                                                                                               }).length;
                                                                                                                                                                                           })()}</div>
                                                                                                                                                                                           <div
                                                                                                                                                                                               className="h6 text-white text-opacity-90">Cloud
                                                                                                                                                                                               Providers
                                                                                                                                                                                           </div>
                                                                                                                                                                                       </div>
                                                                                                                                                                                   </div>


                                                                                                                                                                               </div>
                                                                                                                                                                               <div
                                                                                                                                                                                   className="metric-breakdown">
                                                                                                                                                                                   {Object.entries(dashboardStats.octoAppsByProvider).map(([provider, providerData]) => (
                                                                                                                                                                                       <div
                                                                                                                                                                                           key={provider}
                                                                                                                                                                                           className="breakdown-section mb-2 p-2 bg-light bg-opacity-90 rounded-3">
                                                                                                                                                                                           <div
                                                                                                                                                                                               className="d-flex align-items-center justify-content-between mb-2">
                                         <span className="fw-bold text-dark small text-capitalize">
                                             {provider}
                                         </span>
                                                                                                                                                                                               <span
                                                                                                                                                                                                   className="badge bg-primary bg-opacity-75 fs-6 px-2 py-1">
                                              {(() => {
                                                  // Exclude internal apps from total count
                                                  return Object.entries(providerData.byTypology)
                                                      .filter(([typology]) => !['internal'].includes(typology.toLowerCase()))
                                                      .reduce((sum, [, count]) => sum + count, 0);
                                              })()}
                                          </span>
                                                                                                                                                                                           </div>

                                                                                                                                                                                           <div
                                                                                                                                                                                               className="row g-1">
                                                                                                                                                                                               {/* POV Apps for this provider */}
                                                                                                                                                                                               <div
                                                                                                                                                                                                   className="col-4">
                                                                                                                                                                                                   <div
                                                                                                                                                                                                       className="metric-counter bg-warning bg-opacity-75 rounded-2 p-1 text-center">
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="h6 fw-bold text-dark mb-0">
                                                                                                                                                                                                           {Object.entries(providerData.byTypology)
                                                                                                                                                                                                               .filter(([typology]) => ['pov', 'proof-of-concept', 'proofofconcept'].includes(typology.toLowerCase()))
                                                                                                                                                                                                               .reduce((sum, [, count]) => sum + count, 0)}
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="text-dark fw-semibold small">POV
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                   </div>
                                                                                                                                                                                               </div>

                                                                                                                                                                                               {/* Production Apps for this provider */}
                                                                                                                                                                                               <div
                                                                                                                                                                                                   className="col-4">
                                                                                                                                                                                                   <div
                                                                                                                                                                                                       className="metric-counter bg-success bg-opacity-75 rounded-2 p-1 text-center">
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="h6 fw-bold text-white mb-0">
                                                                                                                                                                                                           {Object.entries(providerData.byTypology)
                                                                                                                                                                                                               .filter(([typology]) => ['production', 'prod', 'live'].includes(typology.toLowerCase()))
                                                                                                                                                                                                               .reduce((sum, [, count]) => sum + count, 0)}
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="text-white fw-semibold small">Production
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                   </div>
                                                                                                                                                                                               </div>

                                                                                                                                                                                               {/* Other Apps for this provider */}
                                                                                                                                                                                               <div
                                                                                                                                                                                                   className="col-4">
                                                                                                                                                                                                   <div
                                                                                                                                                                                                       className="metric-counter bg-info bg-opacity-75 rounded-2 p-1 text-center">
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="h4 fw-bold text-white mb-0">
                                                                                                                                                                                                           {Object.entries(providerData.byTypology)
                                                                                                                                                                                                               .filter(([typology]) => !['pov', 'proof-of-concept', 'proofofconcept', 'production', 'prod', 'live', 'internal'].includes(typology.toLowerCase()))
                                                                                                                                                                                                               .reduce((sum, [, count]) => sum + count, 0)}
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                       <div
                                                                                                                                                                                                           className="text-white fw-semibold small">Other
                                                                                                                                                                                                       </div>
                                                                                                                                                                                                   </div>
                                                                                                                                                                                               </div>
                                                                                                                                                                                           </div>
                                                                                                                                                                                       </div>
                                                                                                                                                                                   ))}
                                                                                                                                                                               </div>
                                                                                                                                                                           </div>
                                                                                                                                                                       </div>
                </motion.div>

                <motion.div
                    className="col-lg-4"
                    initial={{opacity: 0, scale: 0.9}}
                    animate={{opacity: 1, scale: 1}}
                    transition={{delay: 0.4}}
                >
                    <div
                        className="metric-card metric-card-apps text-dark p-4 rounded-4 shadow-lg h-100 position-relative overflow-hidden">
                        <div className="metric-card-bg"></div>
                        <div className="metric-card-content position-relative z-2">
                            <div className="metric-header mb-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="metric-icon-wrapper">
                                        <i className="bi bi-people display-6"></i>
                                    </div>
                                    <div className="metric-number text-end">
                                        <div className="display-3 fw-bold text-white">{totalAppsByVersion}</div>
                                        <div className="h6 text-white text-opacity-90">Customer Apps</div>
                                    </div>
                                </div>
                            </div>

                            <div className="metric-breakdown">
                                {Object.entries(dashboardStats.octoAppsByVersion || {}).map(([version, versionData]) => {
                                    // Skip versions that only have internal apps
                                    if (typeof versionData === 'object' && versionData.byTypology) {
                                        const nonInternalApps = Object.entries(versionData.byTypology)
                                            .filter(([typology]) => !['internal'].includes(typology.toLowerCase()))
                                            .reduce((sum, [, count]) => sum + count, 0);
                                        if (nonInternalApps === 0) return null;
                                    }

                                    const count = typeof versionData === 'object' && versionData.total ? versionData.total : versionData;
                                    return (
                                        <div key={version}
                                             className="breakdown-section mb-2 p-2 bg-light bg-opacity-90 rounded-3">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <span className="fw-bold text-dark small text-capitalize">
                                                    v{version}
                                                </span>
                                                <span className="badge bg-success bg-opacity-75 fs-6 px-2 py-1">
                                                    {(() => {
                                                        if (!versionData.byTypology) return count;
                                                        // Calculate total excluding internal apps
                                                        return Object.entries(versionData.byTypology)
                                                            .filter(([typology]) => !['internal'].includes(typology.toLowerCase()))
                                                            .reduce((sum, [, count]) => sum + count, 0);
                                                    })()}
                                                </span>
                                            </div>


                                            <div className="row g-1">
                                                {/* POV Apps for this version */}
                                                <div className="col-4">
                                                    <div
                                                        className="metric-counter bg-warning bg-opacity-75 rounded-2 p-1 text-center">
                                                        <div className="h6 fw-bold text-dark mb-0">
                                                            {(() => {
                                                                if (!versionData.byTypology) return 0;
                                                                const povCount = Object.entries(versionData.byTypology)
                                                                    .filter(([typology]) => {
                                                                        const lowerTypology = typology.toLowerCase();
                                                                        return ['pov', 'proof-of-concept', 'proofofconcept', 'poc'].includes(lowerTypology);
                                                                    })
                                                                    .reduce((sum, [, count]) => sum + count, 0);
                                                                return povCount;
                                                            })()}
                                                        </div>
                                                        <div className="text-dark fw-semibold small">POV</div>
                                                    </div>
                                                </div>
                                                
                                                {/* Production Apps for this version */}
                                                <div className="col-4">
                                                    <div className="metric-counter bg-success bg-opacity-75 rounded-2 p-1 text-center">
                                                        <div className="h6 fw-bold text-white mb-0">
                                                            {(() => {
                                                                if (!versionData.byTypology) return 0;
                                                                const productionCount = Object.entries(versionData.byTypology)
                                                                    .filter(([typology]) => {
                                                                        const lowerTypology = typology.toLowerCase();
                                                                        return ['production', 'prod', 'live', 'prod'].includes(lowerTypology);
                                                                    })
                                                                    .reduce((sum, [, count]) => sum + count, 0);
                                                                return productionCount;
                                                            })()}
                                                        </div>
                                                        <div className="text-white fw-semibold small">Production</div>
                                                    </div>
                                                </div>
                                                
                                                {/* Other Apps for this version */}
                                                <div className="col-4">
                                                    <div className="metric-counter bg-info bg-opacity-75 rounded-2 p-1 text-center">
                                                        <div className="h6 fw-bold text-white mb-0">
                                                            {(() => {
                                                                if (!versionData.byTypology) return 0;
                                                                const otherCount = Object.entries(versionData.byTypology)
                                                                    .filter(([typology]) => {
                                                                        const lowerTypology = typology.toLowerCase();
                                                                        return !['pov', 'proof-of-concept', 'proofofconcept', 'poc', 'production', 'prod', 'live', 'internal'].includes(lowerTypology);
                                                                    })
                                                                    .reduce((sum, [, count]) => sum + count, 0);
                                                                return otherCount;
                                                            })()}
                                                        </div>
                                                        <div className="text-white fw-semibold small">Other</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                           </div>
                           
                           
                      </div>
                 </motion.div>

                
            </div>

            {/* Content Grid */}
            <div className="row g-4">
                {/* Recent Deployments */}
                <motion.div 
                    className="col-lg-8"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="content-card bg-white rounded-4 shadow-sm border-0">
                        <div className="card-header bg-transparent border-0 p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0 fw-bold">
                                    <i className="bi bi-clock-history me-2 text-primary"></i>
                                    Recent Deployments
                                    {autoRefreshing && (
                                        <span className="ms-2 badge bg-info">
                                            <i className="bi bi-arrow-clockwise me-1"></i>
                                            Auto-refreshing
                                        </span>
                                    )}
                                    {sseConnected && (
                                        <span className="ms-2 badge bg-success">
                                            <i className="bi bi-wifi me-1"></i>
                                            Live Updates
                                        </span>
                                    )}
                                </h4>
                                <button 
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={fetchDashboardData}
                                    disabled={autoRefreshing}
                                >
                                    <i className="bi bi-arrow-clockwise me-1"></i>
                                    Refresh
                                </button>
                            </div>
                            
                            {/* Typologie Filter */}
                            <div className="typologie-filter">
                                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
                                    <label className="form-label mb-0 fw-semibold text-muted">
                                        <i className="bi bi-funnel me-2"></i>
                                        Filter by Typologie:
                                    </label>
                                    <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Typologie filter">
                                        {typologieOptions.map((typologie) => (
                                                                                    <button
                                            key={typologie}
                                            type="button"
                                            className={`btn ${selectedTypologie === typologie ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => handleTypologieChange(typologie)}
                                        >
                                                {typologie === 'ALL' ? 'All Typologies' : typologie.charAt(0).toUpperCase() + typologie.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                    <small className="text-muted">
                                        Showing {filteredDeployments.length} of {recentDeployments.length} deployments
                                    </small>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-4">
                            {recentDeployments?.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th colSpan="8" className="text-center bg-light">
                                                    <small className="text-muted">
                                                        {selectedTypologie === 'ALL' 
                                                            ? 'Showing all typologies' 
                                                            : `Filtered by: ${selectedTypologie.charAt(0).toUpperCase() + selectedTypologie.slice(1)}`
                                                        }
                                                    </small>
                                                </th>
                                            </tr>
                                            <tr>
                                                <th>App</th>
                                                <th>Typology/Cluster</th>
                                                <th>Versions</th>
                                                <th>Status</th>
                                                <th>Duration</th>
                                                <th>Triggered By</th>
                                                <th>Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(filteredDeployments || []).map((deployment, index) => {
                                                const appInfo = getAppInfo(deployment);
                                                return (
                                                    <motion.tr 
                                                        key={deployment.id || index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: 0.1 * index }}
                                                        className={deployment.state === 'running' ? 'table-warning' : ''}
                                                    >
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="bi bi-app me-2 text-primary"></i>
                                                                <strong>{deployment.name}</strong>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                <div><strong>Typology:</strong> {appInfo.typology}</div>
                                                                <div><strong>Cluster:</strong> {appInfo.cluster}</div>
                                                                <div><strong>App:</strong> {appInfo.app}</div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="small">
                                                                {deployment.octoVersion && (
                                                                    <div><strong>Octo:</strong> <span className="badge bg-info">{deployment.octoVersion}</span></div>
                                                                )}
                                                                {deployment.infraVersion && (
                                                                    <div><strong>Infra:</strong> <span className="badge bg-secondary">{deployment.infraVersion}</span></div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge bg-${getStatusColor(deployment.state)}`}>
                                                                <i className={`bi ${getStatusIcon(deployment.state)} me-1`}></i>
                                                                {deployment.state}
                                                                {deployment.state === 'running' && (
                                                                    <span className="ms-1">
                                                                        <i className="bi bi-arrow-clockwise animate-spin"></i>
                                                                    </span>
                                                                )}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {formatDuration(deployment.created_on, deployment.completed_on, deployment.duration)}
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {deployment.triggeredBy || 'System'}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">
                                                                {formatDate(deployment.created_on)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <a 
                                                                    href={deployment.links?.html?.href} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="btn btn-outline-primary btn-sm"
                                                                    title="View in Bitbucket"
                                                                >
                                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                                </a>
                                                                {deployment.state === 'running' && (
                                                                    <button 
                                                                        className="btn btn-outline-danger btn-sm"
                                                                        onClick={() => handleStopPipeline(deployment.id)}
                                                                        title="Stop Pipeline"
                                                                    >
                                                                        <i className="bi bi-stop-fill"></i>
                                                                    </button>
                                                                )}
                                                                <button 
                                                                    className="btn btn-outline-secondary btn-sm"
                                                                    onClick={() => handleUpdatePipelineStatus(deployment.id)}
                                                                    title="Update Status"
                                                                >
                                                                    <i className="bi bi-arrow-clockwise"></i>
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </motion.tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bi bi-inbox display-1 text-muted"></i>
                                    <p className="text-muted mt-3">
                                        {recentDeployments.length > 0 
                                            ? `No deployments found for typologie "${selectedTypologie}"` 
                                            : 'No recent deployments'
                                        }
                                    </p>
                                    {recentDeployments.length > 0 && selectedTypologie !== 'ALL' && (
                                        <button 
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={() => handleTypologieChange('ALL')}
                                        >
                                            <i className="bi bi-arrow-left me-1"></i>
                                            Show All Typologies
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Right Column - Production Apps */}
                <div className="col-lg-4">
                    {/* Reserved Apps moved to header as favorites */}

                    {/* Production Octo Apps */}
                    <motion.div 
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 }}
                    >
                        <div className="content-card bg-white rounded-4 shadow-sm border-0">
                            <div className="card-header bg-transparent border-0 p-4">
                                <h4 className="mb-0 fw-bold">
                                    <i className="bi bi-shield-check me-2 text-success"></i>
                                    Production Apps
                                </h4>
                            </div>
                            <div className="card-body p-4">
                                {dashboardStats.productionOctoApps?.length > 0 ? (
                                    <div className="production-apps-list">
                                        {(dashboardStats.productionOctoApps || []).map((app, index) => (
                                            <motion.div 
                                                key={`${app.app}-${app.cluster}`}
                                                className="production-app-item mb-3 p-3 rounded-3 bg-light"
                                                initial={{ opacity: 0, x: 10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.1 * index }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <h6 className="mb-1 fw-bold">{app.app}</h6>
                                                        <small className="text-muted d-block">{app.cluster}</small>
                                                        <div className="mt-2">
                                                            <span className="badge bg-info me-1">Octo: {app.octoVersion}</span>
                                                            {app.infraVersion && (
                                                                <span className="badge bg-secondary">Infra: {app.infraVersion}</span>
                                                            )}
                                                        </div>
                                                        {app.lastUpdated && (
                                                            <small className="text-muted d-block mt-2">
                                                                Updated: {formatDate(app.lastUpdated)}
                                                                {app.updatedBy && ` by ${app.updatedBy}`}
                                                            </small>
                                                        )}
                                                    </div>
                                                    {app.status && (
                                                        <span className={`badge bg-${getStatusColor(app.status)}`}>
                                                            {app.status}
                                                        </span>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <i className="bi bi-shield-x display-4 text-muted"></i>
                                        <p className="text-muted mt-2">No production Octo apps found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard; 
